import dayjs from "dayjs";
import { useMemo } from "react";
import EncounterOverlay from "../overlay/EncounterOverlay";

import { chartsType } from "App";
import { ConfigType } from "entities/config";
import { EncounterDetails } from "entities/issues";
import { InfoTypes } from "entities/patientInformation";
import { VitalsChartData } from "hooks/types";
import { EncounterWidth } from "utils/constants";
import { EncounterOverlayHeightType } from "../overlay/type";
import TileGroup from "../tiles/TileGroup";
import { ExtendedInfoType, TabTypes } from "../types";

type MonthTimelineProps = {
  tabs?: TabTypes[];
  maxWidth: number;
  configData?: ConfigType;
  groupPatientData: Record<InfoTypes, ExtendedInfoType[]>;
  days: string[];
  encounters: Record<string, EncounterDetails[]>;
  showEncounters: string;
  onTileClick: (patientInfo?: ExtendedInfoType) => void;
  handleToggleSoapNotes: (encounter?: EncounterDetails) => void;
  encounterSectionHeight: EncounterOverlayHeightType;
  searchTerm: string;
  handleShowProcedueOrders: (encounterId: string) => void;
  labView: "lineChart" | "heatMap";
  vitalsChartData: VitalsChartData;
  chartsToDisplay: string[];
  handleToggleDisplayCharts: (chart: chartsType) => void;
};

type HeaderTileProps = {
  groups: string[];
  width: number;
  encounters: Record<string, EncounterDetails[]>;
};

const HeaderTile = ({ groups, width, encounters }: HeaderTileProps) => {
  return (
    <div
      className="flex w-full"
      style={{
        height: `33px`,
        width: width,
      }}
    >
      {groups?.map((item, i) => {
        const singleWidth = encounters[item].length * EncounterWidth + 300;
        return (
          <div
            key={item + i}
            className="font-bold text-lg "
            style={{
              minWidth: singleWidth,
              maxWidth: singleWidth,
            }}
          >
            {dayjs(item).format("MM-DD-YYYY")}
          </div>
        );
      })}
    </div>
  );
};

const DayTimeline = ({
  tabs,
  days,
  maxWidth,
  configData,
  groupPatientData,
  encounters,
  showEncounters,
  labView,
  onTileClick,
  handleToggleSoapNotes,
  encounterSectionHeight,
  searchTerm,
  handleShowProcedueOrders,
  vitalsChartData,
  handleToggleDisplayCharts,
  chartsToDisplay,
}: MonthTimelineProps) => {
  const problemsRowCount = useMemo(() => {
    let count = 0;
    Object.keys(groupPatientData).forEach((key) => {
      count += groupPatientData[key as InfoTypes].length + 1;
    });
    return count + 1;
  }, [groupPatientData]);
  return (
    <>
      <HeaderTile groups={days} width={maxWidth} encounters={encounters} />

      {showEncounters === "true" && (
        <EncounterOverlay
          view="day"
          groups={days}
          width={maxWidth}
          encounters={encounters}
          encounterHeight={problemsRowCount}
          handleToggleSoapNotes={handleToggleSoapNotes}
          encounterSectionHeight={encounterSectionHeight}
          vitals={configData?.vitals}
          tests={configData?.tests}
          handleShowProcedueOrders={handleShowProcedueOrders}
        />
      )}

      <div className="bg-black">
        {tabs?.map((tab, i) => (
          <TileGroup
            isCollapsed={tab.isCollapsed}
            tests={configData?.tests}
            vitals={configData?.vitals}
            groupData={groupPatientData[tab.type]}
            type={tab.type}
            maxWidth={maxWidth}
            key={i}
            groups={days}
            encounters={encounters}
            view="day"
            onTileClick={onTileClick}
            searchTerm={searchTerm}
            labView={labView}
            vitalsChartData={vitalsChartData}
            chartsToDisplay={chartsToDisplay}
            handleToggleDisplayCharts={handleToggleDisplayCharts}
          />
        ))}
      </div>
    </>
  );
};

export default DayTimeline;
